import React, { useMemo, useRef } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
/** Redux */
import { useSelector } from 'react-redux';
import { extractModalStateFromAnalysis } from '../../../redux/selectors/analyses';
/** Mouse handlers hook */
/** Events handler */
import useIncidents from '../hooks/useIncidents';
/** Chart format hook */
import useYAxisFormat from '../hooks/useYAxisFormat';
/** CHART COMPONENTS */
import CustomTooltip from '../CustomTooltip';
import CustomXAxisTick from '../CustomXAxisTick';
import CustomYAxisTick from '../CustomYAxisTick';
import CustomLineDot from '../CustomLineDot';
/** UTILS */
import { isNil } from 'lodash';
import { CHART_MARGIN, DEFAULT_X_AXIS_CONFIG } from '../constants/utils';
import {
  useChartLines,
  usePrepareCohort,
  useSingleChartDataTooltip,
  useYAxisBounds,
  useYAxisWidth,
} from '../hooks';
import { assertCondition } from '../../../utils/types';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import CustomReferenceDotLabel from '../CustomReferenceDotLabel';
import { getFunnelTooltipOverride } from '../FunnelChart/utils';
import { bootstrapTimezoneSelector } from '../../../redux/selectors/bootstrap';
import { useAppSelector } from '../../../redux/hooks';
import { getFunnelTimeToConvertStatistics } from '../FunnelTimeToConvertStats/utils';
import FunnelTimeToConvertStats from '../FunnelTimeToConvertStats';
import { layout } from '../../../config/styles';
import {
  getFunnelDataKeysPerLine,
  MeasureMode,
} from '../FunnelChart/dataKeysUtils';
import { YAxisPosition } from '../interfaces';
import ResponsiveChartWrapper from '../ResponsiveChartWrapper';
import useBuilderInfoDisplay from '../utils/useBuilderInfoDisplay';
import { ChartMode } from '../types';
import SingleChartDataTooltip from '../SingleChartDataTooltip';
import { useTooltipWheelContext } from '../CustomTooltip/CustomTooltipWheelContext';
import {
  CHART_OPACITY_INACTIVE,
  CHART_OPACITY_ACTIVE,
} from '../hooks/useBarChartOpacity';
import useCacheParsedChartLlmRawDataEffect from '../hooks/llmCacheRawData/useCacheParsedChartLlmRawDataEffect';
import { useFloatingLayerContext } from '../../FloatingManager';
import PopperPortal from '../CustomTooltip/PopperPortal';
import { findXAxisLabelIndex } from '../utils/utils';
import { useChartContext } from '../ChartContext';
interface IProps {
  analysisId: string;
  mode: ChartMode;
}

export default React.memo(({ analysisId, mode }: IProps) => {
  const { tooltipContentRef } = useTooltipWheelContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const modalsStateMemoizedPropSelector = useMemo(
    extractModalStateFromAnalysis,
    [],
  );

  const orgTimezone = useAppSelector(bootstrapTimezoneSelector);

  /* Chart modals state */
  const isCreateIncidentModalOpen = useSelector((state) =>
    modalsStateMemoizedPropSelector(
      state,
      analysisId,
      'isCreateIncidentModalOpen',
    ),
  );
  const createIncidentData = useSelector((state) =>
    modalsStateMemoizedPropSelector(state, analysisId, 'createIncidentData'),
  );

  const {
    chartData,
    settings: { chartSettings, chartWidth, incidents },
    metadata: { isBreakdownOverall },
    displayedSlices,
    colorConfig,
    displayedMeasures,
  } = useChartContext();
  const { activeIncidentTypes, axisSettings } = chartSettings;
  // narrow down chartData to IParsedChartDataFunnel
  assertCondition(chartData.queryType === QueryType.Funnel);

  const { chartType } = chartSettings;
  const { linesMeta, data, xAxisKey } = chartData;
  const isCompactMode = mode === ChartMode.Compact;

  const { visibleLines, displayedHighlights } = useChartLines({
    chartSettings,
    chartData,
    isBreakdownOverall,
    colorConfig,
  });

  const statistics = getFunnelTimeToConvertStatistics(
    visibleLines,
    chartData,
    isBreakdownOverall,
    colorConfig,
  );

  const chartDates = useMemo(
    () => data.map((singleDataPoint) => singleDataPoint[xAxisKey] as string),
    [data, xAxisKey],
  );

  const { renderIncidents, renderCreateIncidentReferenceLine } = useIncidents({
    analysisId,
    incidents,
    createIncidentData,
    isCreateIncidentModalOpen,
    chartDates,
    activeIncidentTypes,
    yAxisId: 0,
  });

  const currentFormat = useYAxisFormat({
    forcePercent: chartSettings.isConversionView,
  });

  const dataKeysPerLine = getFunnelDataKeysPerLine(
    chartData,
    chartSettings,
    visibleLines,
    MeasureMode.ALL,
  );

  const funnelTooltipOverride = getFunnelTooltipOverride(
    chartData,
    dataKeysPerLine,
  );

  const { getYAxisDomain } = useYAxisBounds();
  const builderInfoDisplay = useBuilderInfoDisplay(analysisId);

  const {
    onLineDotMouseEnter,
    onMouseLeave,
    tooltipProps,
    clearLockedTooltipProps,
    lockTooltipPositionHandler,
    isTooltipLocked,
  } = useSingleChartDataTooltip({
    containerRef,
  });
  const { handleCreateCohortFromFunnel } = usePrepareCohort({
    analysisId,
  });
  const tooltipZIndex = useFloatingLayerContext();

  // XXX also cache the raw chart numbers in redux, to be used as a context sent to the LLM
  useCacheParsedChartLlmRawDataEffect({
    chartTypeName: 'Comparative Line',
    parsedChartDataRecords: data,
    analysisId,
  });

  // Calculate the Y axis width dynamically
  const yAxisWidth = useYAxisWidth();

  return (
    <>
      <ResponsiveChartWrapper analysisId={analysisId} mode={mode}>
        <ResponsiveContainer ref={containerRef}>
          <LineChart
            data={data}
            margin={CHART_MARGIN}
            // edge case: when the dot is cut by the border of chart, mouseLeaveOnDot will not be triggered
            onMouseLeave={onMouseLeave}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey={xAxisKey}
              {...DEFAULT_X_AXIS_CONFIG}
              tick={(props) => (
                <CustomXAxisTick {...props} shouldRotate={false} />
              )}
            />
            <YAxis
              padding={{ top: 3, bottom: 0 }}
              hide={!axisSettings.yAxisSettings[YAxisPosition.LEFT].isVisible}
              tickSize={0}
              domain={getYAxisDomain({
                yAxisPosition: YAxisPosition.LEFT,
              })}
              allowDataOverflow={true}
              axisLine={false} // Whether to display the Y axis vertical line or not
              tick={
                <CustomYAxisTick
                  measureDisplayFormat={currentFormat}
                  forcePercent={chartSettings.isConversionView}
                />
              }
              tickCount={5}
              tickMargin={10}
              width={yAxisWidth}
            />
            <Tooltip
              offset={20}
              wrapperStyle={{
                zIndex: tooltipZIndex,
                position: mode === ChartMode.Compact ? 'fixed' : 'absolute',
              }}
              allowEscapeViewBox={{ x: false, y: true }}
              content={(props: any) =>
                isNil(tooltipProps) ? (
                  <PopperPortal active={props.active}>
                    {CustomTooltip({
                      props,
                      lines: visibleLines,
                      chartType,
                      chartValues: data,
                      linesMeta,
                      timezone: orgTimezone,
                      forceBothValues: true,
                      xAxisKey,
                      displayedMeasures,
                      displayedSlices,
                      ...funnelTooltipOverride,
                      additionalLabel: builderInfoDisplay,
                      colorConfig,
                      isBreakdownOverall,
                      tooltipPayloadContentRef: tooltipContentRef,
                    })}
                  </PopperPortal>
                ) : null
              }
            />
            {renderIncidents}
            {renderCreateIncidentReferenceLine}
            {visibleLines.map((line, index) => {
              const dataKeys = dataKeysPerLine[line.name];
              const dataKey = chartSettings.isConversionView
                ? dataKeys?.percentKey
                : dataKeys?.valueKey;

              // in case of single date, all slices might be disabled thus we don't have any data
              if (isNil(dataKey)) {
                return null;
              }

              const lineOpacity =
                !tooltipProps || tooltipProps?.line?.name === line.name
                  ? CHART_OPACITY_ACTIVE
                  : CHART_OPACITY_INACTIVE;
              return (
                <Line
                  isAnimationActive={false}
                  key={`line-${index}`}
                  name={line.name}
                  dataKey={dataKey}
                  stroke={line.color}
                  strokeDasharray={line.type === 'dashed' ? '3 5' : '1 0'}
                  strokeWidth={2}
                  opacity={lineOpacity}
                  dot={(props) => (
                    <CustomLineDot
                      {...props}
                      line={line}
                      data={data}
                      chartType={chartType}
                      active={
                        isTooltipLocked &&
                        tooltipProps?.line?.name === line.name &&
                        tooltipProps?.date === props.payload[xAxisKey]
                      }
                      opacity={lineOpacity}
                    />
                  )}
                  activeDot={(props: any) => {
                    return (
                      <CustomLineDot
                        {...props}
                        line={line}
                        xAxisKey={xAxisKey}
                        chartType={chartType}
                        onMouseOver={() => {
                          onLineDotMouseEnter(
                            {
                              x: props.cx,
                              y: props.cy,
                              date: props.payload[xAxisKey],
                              xAxisKey,
                              xAxisLabelIndex: findXAxisLabelIndex(
                                props.payload[xAxisKey],
                                chartDates,
                              ),
                            },
                            line,
                          );
                        }}
                        onMouseLeave={onMouseLeave}
                        onDotClick={lockTooltipPositionHandler}
                        opacity={lineOpacity}
                      />
                    );
                  }}
                />
              );
            })}
            {displayedHighlights.map((highlight, index) => {
              return (
                <ReferenceDot
                  label={CustomReferenceDotLabel}
                  className={`highlight-dot`}
                  key={`dot-${index}`}
                  isFront={true}
                  x={highlight.date}
                  y={highlight.value}
                  r={5}
                  fill="#ff625a"
                  stroke="white"
                  strokeWidth={1}
                  onClick={lockTooltipPositionHandler}
                  onMouseOver={(props: any) => {
                    if (!props.cx || !props.cy) {
                      return;
                    }
                    onLineDotMouseEnter(
                      {
                        x: props.cx,
                        y: props.cy,
                        date: highlight.date,
                        xAxisKey,
                      },
                      highlight.relatedLine,
                      highlight,
                    );
                  }}
                  onMouseLeave={onMouseLeave}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </ResponsiveChartWrapper>
      {tooltipProps && (
        <SingleChartDataTooltip
          analysisId={analysisId}
          handleCreateCohort={handleCreateCohortFromFunnel}
          tooltipProps={tooltipProps}
          isTooltipLocked={isTooltipLocked}
          clearLockedTooltipProps={clearLockedTooltipProps}
          funnelTooltipContext={funnelTooltipOverride.tooltipContext}
        />
      )}
      {!isCompactMode && !isNil(statistics) && (
        <FunnelTimeToConvertStats
          statistics={statistics}
          isCompactMode={chartWidth <= layout.sliceContainerMinWidth}
        />
      )}
    </>
  );
});
